import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { useTournamentSlug } from "../../context/TournamentRouteContext";
import { tournamentPath } from "../../utility/tournamentUrls";
import { afterDoublesBowlerCopy } from "../../utility/doublesSinglesSetup";
import { fetchDoublesSinglesSetup } from "../../utility/doublesSinglesSetupApi";
import {
  EVENT_RULES_EMPTY,
  EVENT_RULES_HEADING,
  buildEventRulesPage,
  hasPostedEventRules,
  processEventRulesRow,
} from "../../utility/eventRules";
import { laneRulesBowlerLines } from "../../utility/laneAssignment";
import { fetchTournamentLaneSettings } from "../../utility/laneAssignmentApi";

const itemHasOwnRules = (item) =>
  Boolean(item.format || item.placement || item.fees || item.money);

const EventRulesBlock = ({ item }) => (
  <section className="py-3">
    <div className="flex items-baseline justify-between gap-4">
      <h3 className="font-display font-bold text-brand-charcoal">{item.name}</h3>
      {item.money ? (
        <div className="font-score text-lg text-brand-charcoal tabular-nums flex-shrink-0">
          {item.money}
        </div>
      ) : null}
    </div>
    {item.format ? (
      <p className="text-sm text-brand-muted mt-1">{item.format}</p>
    ) : null}
    {item.placement ? (
      <p className="text-sm text-brand-charcoal mt-2">{item.placement}</p>
    ) : null}
    {item.fees ? (
      <p className="text-xs text-brand-muted mt-2">{item.fees}</p>
    ) : null}
  </section>
);

const RulesGroup = ({ group }) => {
  const shared = [group.sharedPlacement, group.sharedEach, group.sharedFees]
    .filter(Boolean)
    .join(". ");
  const namesOnly = group.items.length > 0 && !group.items.some(itemHasOwnRules);

  return (
    <section>
      {group.title ? (
        <h3 className="heading-display text-base text-brand-charcoal">
          {group.title}
        </h3>
      ) : null}
      {shared ? (
        <p className="text-sm text-brand-muted mt-1">{shared}</p>
      ) : null}
      {namesOnly ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-0.5 mt-2">
          {group.items.map((item) => (
            <p key={item.id} className="text-brand-charcoal">
              {item.name}
            </p>
          ))}
        </div>
      ) : (
        <div className={group.title || shared ? "mt-1" : undefined}>
          {group.items.map((item) => (
            <EventRulesBlock key={item.id} item={item} />
          ))}
        </div>
      )}
    </section>
  );
};

const TournamentEventRules = ({
  tournamentId,
  framed = true,
  showHeading = true,
}) => {
  const tournamentSlug = useTournamentSlug();
  const [page, setPage] = useState({ intro: "", introLines: [], groups: [] });
  const [posted, setPosted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRules = async () => {
      if (!tournamentId) return;
      try {
        setLoading(true);
        setError(null);
        const [
          { data: tournamentRow, error: tournamentError },
          { data: eventsData, error: eventsError },
          { data: templatesData, error: templatesError },
          { data: bracketsData, error: bracketsError },
          doublesSetup,
          laneSettings,
        ] = await Promise.all([
          supabase
            .from("tournaments")
            .select(
              "handicapBase, handicapPercentage, handicapMax, payoutRatioNumerator, payoutRatioDenominator"
            )
            .eq("id", tournamentId)
            .single(),
          supabase
            .from("tournamentEvents")
            .select(
              `
                id,
                eventName,
                isPrimary,
                bowlerQuantity,
                gamesPerEvent,
                maxSharedPlayers,
                tournamentEventCalculationType (
                  id,
                  name,
                  code
                ),
                eventFee (
                  id,
                  amount,
                  fees (
                    feeName
                  )
                )
              `
            )
            .eq("tournamentID", tournamentId)
            .eq("isDeleted", false)
            .is("templateId", null)
            .order("isPrimary", { ascending: false })
            .order("eventName"),
          supabase
            .from("eventSquadTemplate")
            .select(
              `
                id,
                eventName,
                maxSharedPlayers,
                tournamentEventCalculationType (
                  id,
                  name,
                  code
                ),
                eventSquadTemplateFees (
                  id,
                  amount,
                  fees (
                    feeName
                  )
                )
              `
            )
            .eq("tournamentID", tournamentId)
            .eq("isDeleted", false)
            .order("eventName"),
          supabase
            .from("bracketProduct")
            .select("id, name, entryAmount")
            .eq("tournamentID", tournamentId)
            .eq("isDeleted", false)
            .order("createdAt", { ascending: true }),
          fetchDoublesSinglesSetup(tournamentId).catch(() => null),
          fetchTournamentLaneSettings(tournamentId).catch(() => null),
        ]);

        if (tournamentError) throw tournamentError;
        if (eventsError) throw eventsError;
        if (templatesError) throw templatesError;

        const tournamentEvents = (eventsData || []).map(processEventRulesRow);
        const templates = (templatesData || []).map((template) =>
          processEventRulesRow({
            ...template,
            isPrimary: false,
            isTemplate: true,
            bowlerQuantity: 1,
            eventFee: template.eventSquadTemplateFees,
          })
        );
        const events = [...tournamentEvents, ...templates];
        const hasDoubles = events.some(
          (event) => Number(event.bowlerQuantity) === 2
        );
        setPosted(hasPostedEventRules(events) || Boolean(bracketsData?.length));
        setPage(
          buildEventRulesPage({
            events,
            brackets: bracketsError ? [] : bracketsData || [],
            tournament: tournamentRow,
            afterDoubles: doublesSetup
              ? afterDoublesBowlerCopy(doublesSetup)
              : "",
            laneLines: laneSettings
              ? laneRulesBowlerLines(laneSettings, { hasDoubles })
              : [],
          })
        );
      } catch (err) {
        console.error("Error fetching event rules:", err);
        setError("Failed to load event rules");
      } finally {
        setLoading(false);
      }
    };

    fetchRules();
  }, [tournamentId]);

  const body = loading ? (
    <div className="space-y-3 animate-pulse py-2">
      <div className="h-4 bg-brand-charcoal/10 rounded w-1/2" />
      <div className="h-4 bg-brand-charcoal/10 rounded w-2/3" />
      <div className="h-4 bg-brand-charcoal/10 rounded w-1/3" />
    </div>
  ) : error ? (
    <p className="text-red-500 text-center py-4">{error}</p>
  ) : !posted ? (
    <p className="text-brand-muted">{EVENT_RULES_EMPTY}</p>
  ) : (
    <div className="space-y-5">
      {page.introLines.length ? (
        <div className="text-sm text-brand-charcoal pb-4 border-b border-brand-charcoal/10 space-y-1">
          {page.introLines.map((line) => (
            <p key={line}>{line}</p>
          ))}
        </div>
      ) : null}
      {page.groups.map((group) => (
        <RulesGroup key={group.key} group={group} />
      ))}
    </div>
  );

  if (!framed) {
    return (
      <div>
        {showHeading ? (
          <h2 className="heading-display text-xl mb-4">{EVENT_RULES_HEADING}</h2>
        ) : null}
        {body}
      </div>
    );
  }

  return (
    <div className="card-board p-6">
      {showHeading ? (
        <div className="flex items-baseline justify-between gap-3 mb-4">
          <h2 className="heading-display text-xl">{EVENT_RULES_HEADING}</h2>
          <Link
            to={tournamentPath(tournamentSlug)}
            className="text-sm font-medium text-brand-gold-ink hover:underline"
          >
            Overview
          </Link>
        </div>
      ) : null}
      {body}
    </div>
  );
};

export default TournamentEventRules;
